"use client";

import { useCallback, useEffect, useRef } from "react";
import { getMapConfig } from "@/lib/map-config";
import { getMapRadarSize } from "@/lib/map-config";
import { createCoordinateTransform } from "@/lib/replay-utils";
import type { ReplayData } from "@/lib/demo-parser";
import { drawDroppedBomb, drawPlantedBomb } from "./replay-canvas/bomb-renderers";
import { drawDamageIndicators, drawDeathMarkers, drawWeaponFires } from "./replay-canvas/combat-renderers";
import { drawGrenadeEffects, drawGrenadeTrajectories } from "./replay-canvas/grenade-renderers";
import { drawTopBar } from "./replay-canvas/hud-renderers";
import { drawPlayers } from "./replay-canvas/player-renderers";

interface UseReplayCanvasOptions {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  replayData: ReplayData | null;
  mapImageRef: React.RefObject<HTMLImageElement | null>;
  mapImageLoaded: boolean;
  zoomRef: React.RefObject<number>;
  panOffsetRef: React.RefObject<{ x: number; y: number }>;
}

// ─── Hook ──────────────────────────────────────────────────────────

/**
 * Provides an imperative `drawFrame(frameIndex)` function for canvas rendering.
 * Call this directly from the RAF loop — no React state involved.
 */
export function useReplayCanvas({
  canvasRef,
  replayData,
  mapImageRef,
  mapImageLoaded,
  zoomRef,
  panOffsetRef,
}: UseReplayCanvasOptions) {
  // Cache the latest refs for use inside drawFrame callback
  const replayDataRef = useRef(replayData);
  const mapImageLoadedRef = useRef(mapImageLoaded);

  useEffect(() => {
    replayDataRef.current = replayData;
  }, [replayData]);

  useEffect(() => {
    mapImageLoadedRef.current = mapImageLoaded;
  }, [mapImageLoaded]);

  const drawFrame = useCallback(
    (frameIndex: number) => {
      const data = replayDataRef.current;
      const canvas = canvasRef.current;
      if (!data || !canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const frame = data.frames[frameIndex];
      if (!frame) return;

      const mapConfig = getMapConfig(data.mapName);

      // Apply zoom + pan transform for the world layer
      const zoom = zoomRef.current ?? 1;
      const pan = panOffsetRef.current ?? { x: 0, y: 0 };

      // Clear full canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ── Dark base + dotted background (unzoomed, always fills the canvas) ──
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = "#0B0D12";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const dotSpacing = 20;
      ctx.fillStyle = "rgba(255, 255, 255, 0.06)";
      for (let dx = 0; dx <= canvas.width; dx += dotSpacing) {
        for (let dy = 0; dy <= canvas.height; dy += dotSpacing) {
          ctx.beginPath();
          ctx.arc(dx, dy, 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();

      // ── Zoomed world layer (map image + all game elements) ──
      ctx.save();
      // pivot zoom around canvas center
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      ctx.translate(cx + pan.x, cy + pan.y);
      ctx.scale(zoom, zoom);
      ctx.translate(-cx, -cy);

      // Draw map image inside the zoomed layer so it moves with everything else
      if (mapImageLoadedRef.current && mapImageRef.current && mapConfig) {
        const radarSize = getMapRadarSize(mapConfig);
        const padding = 50;
        const availableWidth = canvas.width - padding * 2;
        const availableHeight = canvas.height - padding * 2;
        const renderScale = Math.min(availableWidth / radarSize, availableHeight / radarSize);
        const mapWidth = radarSize * renderScale;
        const mapHeight = radarSize * renderScale;
        ctx.globalAlpha = 0.7;
        ctx.drawImage(mapImageRef.current, padding, padding, mapWidth, mapHeight);
        ctx.globalAlpha = 1.0;
      }

      // Create coordinate transform (same as before, no zoom baked in)
      const { toScreenX, toScreenY } = createCoordinateTransform(
        canvas.width,
        canvas.height,
        mapConfig,
        frame.players
      );

      const roundStartTick = data.roundStartTicks[frame.roundNumber - 1] || 0;

      // Draw world events
      drawWeaponFires(ctx, data.weaponFires, frame.tick, frame.players, toScreenX, toScreenY);

      // Build flashed players set
      const flashedPlayers = new Set<string>();
      if (data.playerBlinds) {
        for (const blind of data.playerBlinds) {
          const blindEndTick = blind.tick + blind.blindDuration * data.tickRate;
          if (blind.tick <= frame.tick && frame.tick <= blindEndTick) {
            flashedPlayers.add(blind.victimSteamId);
          }
        }
      }

      drawGrenadeEffects(ctx, data.grenades, frame.tick, data.tickRate, toScreenX, toScreenY);
      if (data.grenadeTrajectories?.length) {
        drawGrenadeTrajectories(ctx, data.grenadeTrajectories, frame.tick, roundStartTick, toScreenX, toScreenY);
      }
      drawDeathMarkers(ctx, data.deaths, roundStartTick, frame.tick, toScreenX, toScreenY);
      if (data.bombsPlanted) {
        drawPlantedBomb(ctx, data.bombsPlanted, roundStartTick, frame.tick, toScreenX, toScreenY);
      }
      if (data.bombDrops) {
        drawDroppedBomb(ctx, data.bombDrops, data.bombsPlanted ?? [], data.bombPickupTicks ?? [], frame.tick, toScreenX, toScreenY);
      }
      drawDamageIndicators(ctx, data.hurtEvents ?? [], frame.players, frame.tick, toScreenX, toScreenY);
      drawPlayers(ctx, frame.players, flashedPlayers, toScreenX, toScreenY);

      ctx.restore();

      // ── Fixed HUD (top bar — not affected by zoom) ──
      drawTopBar(ctx, canvas, frame, roundStartTick, data);
    },
    [canvasRef, mapImageRef, zoomRef, panOffsetRef]
  );

  return { drawFrame };
}
